import React from 'react';
import type { ConflitoPagador } from '../../../src/lib/pagadorBoleto';
import { palavrasDoNome } from '../../../src/lib/nomePagador';
import { RESSALVA_MIN_CARACTERES } from '../../../src/lib/ressalvaPagador';

interface Props {
    conflito: ConflitoPagador | null;
    nomeEmitido: string;
    /** Motivo de quem emite mesmo com pessoas diferentes no documento (#6160). */
    ressalva: string;
    onRessalvaChange: (texto: string) => void;
    /** Troca o nome do boleto pelo nome oficial do documento. */
    onUsarNomeOficial: (nome: string) => void;
}

const mesmoTexto = (a: string, b: string) => palavrasDoNome(a).join(' ') === palavrasDoNome(b).join(' ');

/**
 * Texto do aviso de CPF/CNPJ já usado com outro nome, nas duas emissões
 * (fatura e avulso). Com o nome oficial da Receita (#6160), mostra de quem é o
 * documento e oferece usar esse nome no boleto.
 */
const ConflitoPagadorAviso: React.FC<Props> = ({ conflito, nomeEmitido, ressalva, onRessalvaChange, onUsarNomeOficial }) => {
    const oficial = conflito?.nomeOficial || '';
    return (
        <div className="text-left">
            <p className="mb-2">
                O último boleto deste CPF/CNPJ saiu em nome de{' '}
                <strong>{conflito?.nomeRegistrado}</strong> (fatura {conflito?.faturaId}
                {conflito?.numCobranca ? `, boleto ${conflito.numCobranca}` : ''}).
            </p>
            <p className="mb-2">
                O Sicoob guarda <strong>um cadastro de pagador por CPF/CNPJ</strong>. Emitir agora como{' '}
                <strong>{nomeEmitido}</strong> troca o nome do pagador de{' '}
                <strong>todos</strong> os boletos desse documento — inclusive os já emitidos.
            </p>
            {oficial && (
                <div className="mb-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600">
                    <p className="text-sm">
                        Dono deste CPF/CNPJ na Receita: <strong>{oficial}</strong>
                    </p>
                    {!mesmoTexto(oficial, nomeEmitido) && (
                        <button
                            type="button"
                            onClick={() => onUsarNomeOficial(oficial)}
                            className="mt-2 px-3 py-1.5 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
                        >
                            Usar nome oficial no boleto
                        </button>
                    )}
                </div>
            )}
            {conflito?.pessoaDiferente ? (
                <>
                    <p className="font-semibold text-red-700 dark:text-red-400 mb-2">
                        {oficial
                            ? 'O nome do boleto não é o do dono deste CPF/CNPJ: o boleto aparece no nome errado, no banco e no comprovante. O ideal é usar o nome oficial ou corrigir o CPF/CNPJ no cadastro da UC.'
                            : 'Os nomes parecem de pessoas diferentes: emitir assim faz o boleto de um cliente aparecer no nome do outro, no banco e no comprovante. O ideal é corrigir o CPF/CNPJ no cadastro da UC.'}
                    </p>
                    <label htmlFor="ressalva-pagador" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Se for emitir assim, explique o porquê <span className="text-red-600">*</span>
                    </label>
                    <textarea
                        id="ressalva-pagador"
                        value={ressalva}
                        onChange={e => onRessalvaChange(e.target.value)}
                        rows={2}
                        placeholder="Ex.: empresa do próprio cliente; CPF do responsável pelo acordo"
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Fica registrado com o seu nome no histórico da fatura. Mínimo de {RESSALVA_MIN_CARACTERES} caracteres.
                    </p>
                </>
            ) : oficial ? (
                <p>
                    O nome do boleto confere com o dono do documento na Receita: pode seguir.
                    O boleto anterior é que saiu com outro nome.
                </p>
            ) : (
                <p>
                    Os nomes parecem do mesmo cliente (grafia diferente, razão social nova): pode seguir.
                    Se forem clientes diferentes, cancele e corrija o cadastro antes.
                </p>
            )}
        </div>
    );
};

export default ConflitoPagadorAviso;
